import { Component } from '@theme/component';

/** Consecutive frames the measured height must repeat before we stop measuring. */
const SETTLE_STABLE_FRAMES = 5;

/** Upper bound on a measuring pass, so a never-settling document cannot spin forever. */
const SETTLE_TIMEOUT_MS = 2000;

/**
 * NewsletterModal - Guatemala modal shell for an isolated, self-contained form.
 * The iframe owns CRM submission, validation and legal content. This component
 * owns the native dialog, focus management, iframe sizing and shell lifecycle.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The modal dialog element
 * @property {HTMLButtonElement} closeBtn - The close button
 * @property {HTMLIFrameElement} frame - The isolated form document
 * @property {HTMLElement} surface - Programmatic focus target
 *
 * @extends Component<Refs>
 */
export default class NewsletterModal extends Component {
  #triggerButton = null;
  #closeTimeoutId = null;
  #autoOpenTimeoutId = null;
  #wasAutoOpened = false;
  #resizeObserver = null;
  #mutationObserver = null;
  #measuring = false;
  #settleFrameId = null;
  #settleDeadline = 0;
  #settleStableFrames = 0;
  #settleLastHeight = -1;
  #boundHandleDialogClick = null;
  #boundHandleKeyDown = null;
  #boundHandleCancel = null;
  #boundHandleFrameMessage = null;
  #boundSyncFrameHeight = null;
  #boundScheduleMeasure = null;
  #boundSettleStep = null;

  /** @type {string[]} */
  requiredRefs = ['dialog', 'closeBtn'];

  connectedCallback() {
    super.connectedCallback();

    this.#triggerButton = this.closest('.newsletter')?.querySelector('.newsletter__button');

    this.#boundHandleDialogClick = this.#handleDialogClick.bind(this);
    this.#boundHandleKeyDown = this.#handleKeyDown.bind(this);
    this.#boundHandleCancel = this.#handleCancel.bind(this);
    this.#boundHandleFrameMessage = this.#handleFrameMessage.bind(this);

    const { frame } = this.refs;
    if (frame) {
      this.#boundSyncFrameHeight = this.#syncFrameHeight.bind(this);
      frame.addEventListener('load', this.#boundSyncFrameHeight);
    }
    window.addEventListener('message', this.#boundHandleFrameMessage);

    // Auto-open on first visit while cookie consent has not been stored.
    this.#autoOpenTimeoutId = window.setTimeout(() => {
      this.#autoOpen();
      this.#autoOpenTimeoutId = null;
    }, 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#closeTimeoutId) {
      clearTimeout(this.#closeTimeoutId);
      this.#closeTimeoutId = null;
    }
    if (this.#autoOpenTimeoutId) {
      clearTimeout(this.#autoOpenTimeoutId);
      this.#autoOpenTimeoutId = null;
    }
    this.#resizeObserver?.disconnect();
    this.#mutationObserver?.disconnect();
    if (this.#settleFrameId !== null) {
      cancelAnimationFrame(this.#settleFrameId);
      this.#settleFrameId = null;
    }
    this.refs.dialog?.removeEventListener('click', this.#boundHandleDialogClick);
    this.refs.dialog?.removeEventListener('cancel', this.#boundHandleCancel);
    this.removeEventListener('keydown', this.#boundHandleKeyDown);
    window.removeEventListener('message', this.#boundHandleFrameMessage);
    if (this.#boundSyncFrameHeight) {
      this.refs.frame?.removeEventListener('load', this.#boundSyncFrameHeight);
    }
    if (this.#boundScheduleMeasure) {
      this.refs.frame?.contentDocument?.removeEventListener('load', this.#boundScheduleMeasure, true);
    }
  }

  /**
   * Syncs the iframe height to its content. `srcdoc` inherits the storefront
   * origin, so the content document is readable without a second integration.
   */
  #syncFrameHeight() {
    const { frame } = this.refs;
    const doc = frame?.contentDocument;
    if (!doc?.body) return;

    this.#boundScheduleMeasure ??= this.#scheduleMeasure.bind(this);
    this.#boundSettleStep ??= this.#settleStep.bind(this);

    this.#scheduleMeasure();

    // Observe only the body. Observing documentElement would re-fire whenever
    // the parent writes a new iframe height and can create a resize loop.
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = new ResizeObserver(this.#boundScheduleMeasure);
    this.#resizeObserver.observe(doc.body);

    // Success/error states change the subtree without always changing the
    // body's immediate box before the next layout pass.
    this.#mutationObserver?.disconnect();
    this.#mutationObserver = new MutationObserver(this.#boundScheduleMeasure);
    this.#mutationObserver.observe(doc.body, { childList: true, subtree: true, attributes: true });

    // Image decoding and font swaps resolve later, and load does not bubble.
    doc.addEventListener('load', this.#boundScheduleMeasure, true);
    this.#postFrameMessage({ type: 'newsletter:reset' });
  }

  /** Starts a bounded measuring pass until the iframe content settles. */
  #scheduleMeasure() {
    this.#settleStableFrames = 0;
    if (this.#settleFrameId !== null) return;

    this.#settleDeadline = performance.now() + SETTLE_TIMEOUT_MS;
    this.#settleFrameId = requestAnimationFrame(this.#boundSettleStep);
  }

  /** Stops once the measured height is stable or the hard deadline expires. */
  #settleStep() {
    const height = this.#measureFrame();

    if (height === this.#settleLastHeight) {
      this.#settleStableFrames += 1;
    } else {
      this.#settleStableFrames = 0;
      this.#settleLastHeight = height;
    }

    if (this.#settleStableFrames >= SETTLE_STABLE_FRAMES || performance.now() > this.#settleDeadline) {
      this.#settleFrameId = null;
      return;
    }

    this.#settleFrameId = requestAnimationFrame(this.#boundSettleStep);
  }

  /**
   * Measures the iframe body while parking the viewport at a stable reference.
   * This prevents `vh` inside srcdoc from chasing the height written by the
   * parent and lets the embedded form use `--modal-vh` for responsive limits.
   *
   * @returns {number} The applied height in pixels, or -1 if it could not measure.
   */
  #measureFrame() {
    const { frame } = this.refs;
    const doc = frame?.contentDocument;
    const view = doc?.defaultView;
    if (!doc?.body || !view || this.#measuring) return -1;

    this.#measuring = true;

    const reference = Math.round(window.innerHeight);
    doc.documentElement.style.setProperty('--modal-vh', `${reference}px`);
    if (frame.style.height !== `${reference}px`) frame.style.height = `${reference}px`;

    const styles = view.getComputedStyle(doc.body);
    const margins = (parseFloat(styles.marginTop) || 0) + (parseFloat(styles.marginBottom) || 0);
    const measured = Math.ceil(doc.body.getBoundingClientRect().height + margins);
    const height = measured > 0 ? measured : reference;

    frame.style.height = `${height}px`;
    this.#measuring = false;

    return height;
  }

  #getFocusableElements() {
    const { dialog } = this.refs;
    if (!dialog) return [];

    return [...dialog.querySelectorAll('button, iframe, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')].filter(
      (element) => !element.hasAttribute('disabled') && !element.hasAttribute('hidden')
    );
  }

  /** Sends lifecycle/focus messages to the same-origin srcdoc document. */
  #postFrameMessage(message) {
    const frame = this.refs.frame;
    if (!frame?.contentWindow) return;
    frame.contentWindow.postMessage(message, window.location.origin);
  }

  /**
   * Opens the modal automatically only while the user has not accepted cookie
   * consent. Both historical GT and shared AE consent cookies are respected.
   */
  #autoOpen() {
    const cookies = document.cookie.split(';').map((cookie) => cookie.trim());
    const hasConsent = cookies.some(
      (cookie) => cookie.startsWith('ae_cookie_consent=') || cookie.startsWith('avada_cookie_consent=')
    );
    const dismissed = cookies.some((cookie) => cookie.startsWith('ae_newsletter_dismissed='));
    if (hasConsent || dismissed) return;

    this.#wasAutoOpened = true;
    this.openModal();
  }

  /** Opens the native modal dialog and focuses the first embedded control. */
  openModal() {
    const { dialog } = this.refs;
    if (!dialog || dialog.open) return;

    dialog.showModal();
    dialog.classList.remove('newsletter-modal__dialog--closing');
    dialog.classList.add('newsletter-modal__dialog--opening');
    dialog.addEventListener('click', this.#boundHandleDialogClick);
    dialog.addEventListener('cancel', this.#boundHandleCancel);
    this.addEventListener('keydown', this.#boundHandleKeyDown);
    this.#postFrameMessage({ type: 'newsletter:reset' });

    this.#closeTimeoutId = window.setTimeout(() => {
      dialog.classList.remove('newsletter-modal__dialog--opening');
      this.#closeTimeoutId = null;
    }, 140);

    requestAnimationFrame(() => {
      this.#syncFrameHeight();
      const frameControl = this.refs.frame?.contentDocument?.querySelector('input:not([type="hidden"]), button, [href], select, textarea');
      const firstFocusable = this.#getFocusableElements().find((element) => element !== this.refs.closeBtn);
      (frameControl ?? firstFocusable ?? this.refs.surface ?? this.refs.closeBtn)?.focus();
    });
  }

  /** Closes the native modal and restores manual-trigger focus. */
  closeModal() {
    const { dialog } = this.refs;
    if (!dialog || !dialog.open || dialog.classList.contains('newsletter-modal__dialog--closing')) return;

    document.cookie = 'ae_newsletter_dismissed=1; max-age=3600; path=/';
    dialog.removeEventListener('click', this.#boundHandleDialogClick);
    dialog.removeEventListener('cancel', this.#boundHandleCancel);
    this.removeEventListener('keydown', this.#boundHandleKeyDown);

    dialog.classList.remove('newsletter-modal__dialog--opening');
    dialog.classList.add('newsletter-modal__dialog--closing');

    this.#closeTimeoutId = window.setTimeout(() => {
      dialog.close();
      dialog.classList.remove('newsletter-modal__dialog--closing');
      this.#closeTimeoutId = null;
      if (!this.#wasAutoOpened) this.#triggerButton?.focus();
      this.#wasAutoOpened = false;
    }, 140);
  }

  /** Handles the native dialog cancel event, including Escape. */
  #handleCancel(event) {
    event.preventDefault();
    this.closeModal();
  }

  /**
   * Handles focus/close messages emitted by the isolated form document.
   *
   * @param {MessageEvent} event
   */
  #handleFrameMessage(event) {
    const frame = this.refs.frame;
    if (event.source !== frame?.contentWindow || event.origin !== window.location.origin) return;

    if (event.data?.type === 'newsletter:focus-close') {
      this.refs.closeBtn?.focus();
    }
    if (event.data?.type === 'newsletter:close') {
      this.closeModal();
    }
  }

  /**
   * Handles backdrop clicks.
   * @param {MouseEvent} event
   */
  #handleDialogClick(event) {
    const { dialog } = this.refs;
    if (!dialog || event.target !== dialog) return;

    const surface = dialog.querySelector('.newsletter-modal__surface');
    if (!surface || !(event instanceof MouseEvent)) {
      this.closeModal();
      return;
    }

    const { left, right, top, bottom } = surface.getBoundingClientRect();
    const clickedBackdrop =
      event.clientX < left ||
      event.clientX > right ||
      event.clientY < top ||
      event.clientY > bottom;

    if (clickedBackdrop) this.closeModal();
  }

  /**
   * Handles top-level focus trap and Escape.
   * @param {KeyboardEvent} event
   */
  #handleKeyDown(event) {
    if (event.key === 'Tab') {
      const focusableElements = this.#getFocusableElements();
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
        return;
      }

      if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
      return;
    }

    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.closeModal();
  }
}

if (!customElements.get('newsletter-modal')) {
  customElements.define('newsletter-modal', NewsletterModal);
}
